import { z } from 'zod'

/**
 * Fleet Temperature Forecaster chart contract.
 *
 * Consumer-side mirror of the ModelFactory chart payload built by
 * `modelfactory/services/fleet_chart_payload.py` (authoritative contract:
 * ModelFactory `docs/models/fleet-temperature-forecaster.md`). Rows share the
 * tundra/Santos shape (Historical = the observed daily 30 cm series; Forecast =
 * the predicted 30 cm trajectory) so the Site chart renders both models with
 * one code path. The fleet band is the OUTER interval only -- P50 + P10/P90;
 * P25/P75 are always null.
 *
 * Meta carries the machine-readable fields the frontend MUST gate rendering on
 * (`status`, `band_status`, `climate_in_envelope`) and the disclaimers
 * ModelFactory emits. The beadedcloud.com site page no longer renders them
 * (owner decision 2026-10-06); the field is retained in the payload. This
 * is a soil-temperature product, NOT the DNR ice-road determination -- there is
 * deliberately no ready-date / DNR-delay field (that is the tundra-readiness
 * service).
 */

export const fleetTemperatureForecastRow = z.object({
  date: z.string(), // 'YYYY-MM-DD', UTC day
  target: z.string(), // '30 cm Soil Temp'
  row_type: z.enum(['Historical', 'Forecast']),
  value: z.number().nullable(), // Historical only
  P10: z.number().nullable(), // Forecast only (band low)
  P25: z.number().nullable(), // always null (no inner band)
  P50: z.number().nullable(), // Forecast median
  P75: z.number().nullable(), // always null (no inner band)
  P90: z.number().nullable(), // Forecast only (band high)
})

export const fleetTemperatureForecastMeta = z.object({
  model_type: z.string().nullable(),
  version: z.string().nullable(),
  as_of: z.string().nullable(),
  site_id: z.number().nullable(),
  leg: z.enum(['fleet-temperature']).nullable(),
  status: z
    .enum(['ok', 'insufficient_history', 'unavailable_data', 'off_season'])
    .nullable(),
  band_status: z.enum(['conformal', 'uncalibrated']).nullable(),
  data_freshness: z.string().nullable(),
  data_freshness_days: z.number().nullable(),
  climate_in_envelope: z.boolean().nullable(),
  band_nominal: z.number().nullable(),
  cold_start: z.boolean().nullable(),
  disclaimers: z.array(z.string()),
})

export const fleetTemperatureForecastChart = z.object({
  rows: z.array(fleetTemperatureForecastRow),
  meta: fleetTemperatureForecastMeta,
})

export type FleetTemperatureForecastRow = z.infer<
  typeof fleetTemperatureForecastRow
>
export type FleetTemperatureForecastMeta = z.infer<
  typeof fleetTemperatureForecastMeta
>
export type FleetTemperatureForecastChart = z.infer<
  typeof fleetTemperatureForecastChart
>
